import type { AckMissing, AckPayloads, ListingState, SharingClosedReason } from '@ralph/contracts'
import type { Locale } from '@/i18n/locale.ts'
import { maker } from '@/i18n/messages/en/marketplace/maker.ts'

/**
 * `marketplace.publish` — turning an agent on, sharing it, its lifecycle, and the two
 * sentences a person is asked to agree to. English is the source of truth and the type.
 *
 * ── Why this is its own namespace ──
 *
 * It is split out of `marketplace.maker` because it is not only copy: two of the sentences
 * below are PERSISTED VERBATIM as the record of what a human was told before they widened
 * something (AGM-042, PRD-27 D-7). `publish.consentSentence` is what the maker agreed to when
 * they shared an agent; `publish.lend.sentence` is what a knowledge base's maintainer agreed to
 * when they let an agent answer from it for people who cannot open it. An auditor reads those
 * rows later, beside the `ackKey`, `ackVersion` and `locale` that make them verifiable. So this
 * file carries a different review bar from the rest of the builder's words, and a reword here
 * is a change to an audit trail rather than to a screen.
 *
 * `lend` lives here rather than under `maker.sections.knowledge` for exactly that reason: it is
 * a consent record that happens to be collected from the Knowledge step.
 *
 * ── The three registers ──
 *
 * Turning it on for its maintainers is not a handover and never says it is. Sharing with people
 * the maker may share with alone IS a handover, and says so. And a choice that includes a group
 * they do not manage, or everyone, is split: what can go live does, and the rest is sent for
 * review — so the dialog states what happens now, what happens if an administrator approves,
 * and what they will get (PRD-20 OWN-012/013, PRD-26 D-13, D-40, D-50).
 *
 * The acknowledgement itself never becomes optional. AGM-042 wants what the maker was told
 * persisted verbatim, and a record that exists for some publications and not others is a worse
 * record. One checkbox, always; the sentences follow the choice.
 *
 * Which sentences to SHOW for a given choice is `maker/publish-wording.ts`'s decision, not this
 * file's — catalogue-consuming logic lives beside the components (LOC-017).
 */

/**
 * Written out structurally rather than as `Pick<I18n, …>`: this file is part of `Messages`,
 * `I18n` holds a `Messages`, and naming `I18n` here would close a type cycle through the
 * aggregator. The shapes are the provider's, so `useI18n()` satisfies them by structure.
 */
type Counting = { locale: Locale; formatCount: (value: number) => string }
type Listing = { formatList: (items: readonly string[]) => string }

/** What the consent sentence needs: it both counts (gap sizes) and joins (bases, groups). */
type Wording = Counting & Listing

/**
 * The disclosure's parameters, named once. Declared in `@ralph/contracts` because they are the
 * protocol between this dialog and the row that keeps what was agreed to (PRD-27 D-7) — the
 * words are this file's, the shape is not.
 */
type PublishAck = AckPayloads['ack.publish']

/**
 * The three sentence parts the disclosure shares with entries beside it.
 *
 * Module-local functions rather than reads of `publish` from inside `publish`, which would make
 * the object reference itself in its own initializer and cost the file the inferred type that
 * `Messages = typeof en` is built from. The catalogue entries below are thin wrappers, so the
 * dialog and the audit replay call one implementation.
 */
const UNKNOWN_KNOWLEDGE = 'a knowledge base that is no longer available to you'
const UNKNOWN_CAPABILITY = 'something that is no longer available'

const ackSubjectOf = (fmt: Listing, audience: PublishAck['audience']): string => {
  if (audience.kind === 'everyone') return 'Everyone who signs in'
  if (audience.kind === 'groups') return `People in ${fmt.formatList(audience.groups) || 'no group yet'}`
  if (audience.privateKind === 'self') return 'Only you'
  if (audience.privateKind === 'group_managers') return `Only ${audience.privateName ?? ''}’s managers`
  return `Only ${audience.privateName ?? ''}`
}

const gapWhomOf = (fmt: Wording, missing: AckMissing): string =>
  missing.kind === 'everyone'
    ? maker.sections.audience.gapEveryone(fmt, missing.people)
    : missing.kind === 'groups'
      ? fmt.formatList(missing.groups.map((group) => maker.sized(fmt, group)))
      : maker.sections.audience.gapPeople(fmt, missing.people)

const gapLine = (base: string, whom: string): string => `${base} will not reach ${whom}: they get answers without it`

export const publish = {
  open: 'Publish',
  openPrivate: 'Turn it on',
  republish: 'Publish changes',
  republishPrivate: 'Update it',
  sendForReview: 'Send for review',
  restore: 'Bring it back',
  title: 'Publish this agent',
  titlePrivate: 'Turn this agent on',
  titleReview: 'Send this agent for review',
  titleRestore: 'Bring this agent back',
  lead: 'This is the moment it becomes other people’s. Read what you are sharing.',
  leadPrivate:
    'It becomes usable — by the people who maintain it, and by nobody else. Read what it will be able to do.',
  leadReview:
    'Sharing with a group you do not manage, or with everyone, waits for an administrator’s review. Read what happens now, and what happens if they approve.',
  leadRestore: 'It comes back for the people you choose. Read what they will get.',
  audienceHeading: 'Who can use it',
  nowHeading: 'Now',
  ifApprovedHeading: 'If an administrator approves',
  consentHeading: 'What they get',
  consentHeadingPrivate: 'What you get',
  /** What happens the moment the maker confirms. */
  now: {
    live: (who: string): string => `${who} can use it as soon as you confirm.`,
    liveVersion: 'Everyone who can use it now gets this version at once.',
    liveChanged: (who: string): string => `This version goes live at once, for ${who}.`,
    waitsDraft: 'Nothing changes until an administrator approves — it stays a draft, and nobody else can use it.',
    waitsArchived: 'Nothing changes until an administrator approves — it stays retired.'
  },
  /** What an approval would add. A review covers who can use it, not each version (D-14). */
  ifApproved: (who: string): string => `${who} can use it too. Later versions reach them without another review.`,
  ifApprovedEveryone: 'Everyone who signs in can use it. Later versions reach them without another review.',
  ackLabel: 'I have read this and I am sharing it.',
  ackLabelPrivate: 'I have read this and I am turning it on.',
  ackLabelReview: 'I have read this and I am sending it for review.',
  ackHint: 'Only the people who maintain it can agree to this, and the agreement is kept with what it applies to.',
  confirm: 'Publish',
  confirmPrivate: 'Turn it on',
  confirmReview: 'Send for review',
  confirmRestore: 'Bring it back',
  cancel: 'Not yet',
  done: 'Done',
  publishing: 'Publishing…',
  sending: 'Sending…',
  publishedTitle: 'Published',
  publishedTitlePrivate: 'It is on',
  publishedTitleRestore: 'It is back',
  publishedBody: (who: string): string => `${who} can use it now.`,
  publishedBodyPrivate: (personal: boolean): string =>
    personal
      ? 'It is in your agent picker now. Nobody else can see it.'
      : 'It is in its managers’ agent pickers now. Nobody else can see it.',
  sentTitle: 'Sent for review',
  sentAndPublishedTitle: 'Published, and sent for review',
  sentBody: (who: string): string =>
    `An administrator will review sharing with ${who}. They see exactly what you just read, and you will be told what they decide.`,
  sentLiveNote: 'Everyone who could already use it has this version now.',
  sentWaitNote: 'Until then it stays as it is.',
  failed: 'It could not be published.',
  nameTakenTitle: 'That name is taken',
  nameTakenBody: 'Another agent already uses this name. Pick a different one and publish again.',
  blockedByLint: 'Fix the findings above first.',
  blockedByAudience: 'Pick who can use it first.',
  blockedUnsaved: 'Save your changes first — publishing shares the saved version.',
  blockedNotOwner: 'Only the people who maintain it can publish it, choose who can use it, or move it.',
  unknownKnowledge: UNKNOWN_KNOWLEDGE,
  unknownCapability: UNKNOWN_CAPABILITY,
  /**
   * The audience as the subject of the disclosure, from the stored shape rather than from a
   * phrase somebody else built (PRD-27 D-7, D-20).
   *
   * `audienceSubject` below does the same job for the dialog, which already holds the picker's
   * private label and passes it in. This one takes the four values the consent record keeps, so
   * that the same subject can be rebuilt months later, in either language, from the row alone.
   * The three `private` forms are the picker's three ways of naming "only its maintainers"
   * (D-37) and they are genuinely different sentences: the self form addresses the reader.
   */
  ackSubject: (fmt: Listing, audience: PublishAck['audience']): string => ackSubjectOf(fmt, audience),
  /**
   * Who a base fails to reach, as the object of "will not reach …".
   *
   * Built here from the counts rather than handed in ready-made: "Legal (8 people)" puts a
   * number, a bracket and a plural noun in one string, and Portuguese puts zero in the singular
   * category — so the phrase has to be assembled in the language that renders it. The words are
   * the builder's, through `maker`, because the Knowledge step says the same thing.
   */
  ackGapWhom: (fmt: Wording, missing: AckMissing): string => gapWhomOf(fmt, missing),
  /**
   * The sentence the maintainer acknowledges — and the record of what they were told (AGM-042,
   * PRD-27 D-7).
   *
   * It is rendered from the same values that are persisted as `ackParams`, which is what makes
   * the record replayable: an auditor reading the Portuguese acknowledgement can feed the stored
   * parameters to this entry's English twin and get the English sentence, without a translator
   * and without trusting that the two catalogues were kept in step by hand.
   *
   * `subject` says WHO the sentence is about — the person reading it, or other people. It
   * replaces a `reader: 'they' | 'you'` parameter, which asked the call site for an English
   * pronoun (D-20): Portuguese carries that distinction on the verb, not in a word that can be
   * slotted in, so the parameter now states the fact and each language decides how to say it.
   *
   * `unknownBases` and `unknownCapabilities` are counts, not the stand-in phrases: "a knowledge
   * base that is no longer available to you" is copy, and a record holding it would be a record
   * that only reads back in English. `gaps` are the coverage gaps for the audience being chosen
   * (GRP-045), said here because "People in Legal will get answers grounded in Payroll Rules" is
   * untrue while they cannot.
   */
  consentSentence: (fmt: Wording, ack: PublishAck): string => {
    const subject = ackSubjectOf(fmt, ack.audience)
    const baseNames = [...ack.bases, ...Array.from({ length: ack.unknownBases }, () => UNKNOWN_KNOWLEDGE)]
    const capabilityNames = [
      ...ack.capabilities,
      ...Array.from({ length: ack.unknownCapabilities }, () => UNKNOWN_CAPABILITY)
    ]
    const knowledge =
      baseNames.length === 0
        ? `answers based on documents ${ack.subject === 'reader' ? 'you' : 'they'} can already use`
        : `answers grounded in ${fmt.formatList(baseNames)}`
    const doing = capabilityNames.length === 0 ? '' : `, and the agent can use ${fmt.formatList(capabilityNames)}`
    const missing = ack.gaps.map((gap) => ` ${gapLine(gap.base, gapWhomOf(fmt, gap.missing))}.`).join('')
    return `${subject} will get ${knowledge}${doing}.${missing}`
  },
  /** A coverage gap, as the consent sentence says it. */
  gapSentence: (base: string, whom: string): string => gapLine(base, whom),
  /** The audience as a sentence subject (D-37: a private label names the people). */
  audienceSubject: (
    fmt: Listing,
    kind: 'private' | 'groups' | 'everyone',
    groups: readonly string[],
    privateLabel: string
  ): string =>
    kind === 'everyone'
      ? 'Everyone who signs in'
      : kind === 'private'
        ? privateLabel
        : `People in ${fmt.formatList(groups) || 'no group yet'}`,
  /** The same audience as a noun phrase, for the line under "Who can use it". */
  audiencePhrase: (
    fmt: Listing,
    kind: 'private' | 'groups' | 'everyone',
    groups: readonly string[],
    privateLabel: string
  ): string =>
    kind === 'everyone'
      ? 'Everyone who signs in'
      : kind === 'private'
        ? privateLabel
        : fmt.formatList(groups) || 'No group yet',
  /**
   * "Only me", said about somebody else's agent (D-37: a private label names the people) —
   * and, in each `Phrase` twin, the same people read mid-line.
   *
   * Two entries rather than one lower-cased at the call site (D-20). Re-casing a built
   * sentence assumes an orthography: it is wrong wherever a name or a noun stays capitalized
   * mid-sentence, and it cannot know which. This is the `audienceSubject`/`audiencePhrase`
   * pair above, applied to the same question one line lower.
   */
  privateOwner: (owner: string): string => `Only ${owner}`,
  privateOwnerPhrase: (owner: string): string => `only ${owner}`,
  /** The same, as the subject of a sentence the maker reads about themselves. */
  privateYou: 'Only you',
  privateYouPhrase: 'only you',
  privateManagers: (group: string): string => `Only ${group}’s managers`,
  privateManagersPhrase: (group: string): string => `only ${group}’s managers`,

  lifecycle: {
    state: {
      draft: 'Draft',
      published: 'Published',
      archived: 'Retired',
      suspended: 'Paused by an administrator'
    } satisfies Record<ListingState, string>,
    /**
     * What each state actually means, said to the person who can change it.
     *
     * The draft line used to read "Only you and your co-editors can use it", which was the
     * single most misleading sentence in the product (PRD-20 G-2): a draft cannot be used by
     * anybody, including its owner — the picker filters it, the listing page will not start a
     * chat with it, and the runtime refuses to resolve it. What it meant was *see*.
     */
    stateHint: {
      draft:
        'Only its maintainers and anyone invited to edit it can see it. Nobody can talk to it yet — try it with a preview, then turn it on.',
      published: 'The people it is shared with can find it.',
      archived: 'Conversations stay readable; nobody can start a new one.',
      suspended: 'An administrator stopped it. They will have said why.'
    } satisfies Record<ListingState, string>,
    /** Published, said in terms of who that actually is (OWN-011). */
    publishedHintPrivate: (personal: boolean): string =>
      personal
        ? 'Only you can use it. It stays out of everyone else’s gallery.'
        : 'Only its managers can use it. It stays out of everyone else’s gallery.',
    unpublish: 'Unpublish',
    unpublishConfirm: 'Take it back to a draft?',
    unpublishBody:
      'People lose it from their gallery. Conversations they already have stay readable, and they cannot send new messages.',
    archive: 'Retire',
    archiveConfirm: 'Retire this agent?',
    archiveBody:
      'It disappears from the gallery, and existing conversations stay readable. You can bring it back later.',
    archiveNameHold: 'Its name stays reserved for a while afterwards, so nobody can pass themselves off as it.',
    /** D-51: taking an agent down closes what was waiting, and whoever sent it is told. */
    closesRequest: 'The request waiting for review is closed, and whoever sent it is told.',
    restore: 'Bring it back',
    confirm: 'Yes, do it',
    cancel: 'Cancel',
    failed: 'That did not work.',

    /**
     * The sharing request, on the bar (PRD-26 GRP-081): waiting, with a way to withdraw it; then
     * its outcome, until whoever reads it dismisses it.
     */
    request: {
      waiting: (whom: string, when: string): string => `Waiting for review: sharing with ${whom}. Sent ${when}.`,
      waitingBy: (name: string): string => `${name} sent it.`,
      withdraw: 'Withdraw request',
      withdrawing: 'Withdrawing…',
      withdrawFailed: 'The request could not be withdrawn.',
      approved: (whom: string): string => `Approved: ${whom} can use it now.`,
      approvedBy: (name: string | null, when: string): string =>
        name === null ? `An administrator approved it ${when}.` : `${name} approved it ${when}.`,
      declined: (whom: string): string => `Not approved: sharing with ${whom}.`,
      declinedBy: (name: string | null, when: string): string =>
        name === null ? `An administrator declined it ${when}.` : `${name} declined it ${when}.`,
      closed: (whom: string): string => `The request to share with ${whom} was closed.`,
      closedReason: {
        group_deleted: 'A group it named was deleted.',
        requester_lost_authority: 'Whoever sent it no longer maintains this agent.',
        item_unavailable: 'The agent was unpublished, retired or paused.',
        migrated: 'It named groups that came from sign-in, which no longer exist here.'
      } satisfies Record<SharingClosedReason, string>,
      note: (note: string): string => `“${note}”`,
      everyone: 'everyone who signs in',
      dismiss: 'Dismiss'
    }
  },

  /**
   * Letting a base answer through an agent (PRD-26 D-16), offered only to someone who maintains
   * the base. The sentence is the decision screen's, stored verbatim as the record of what they
   * allowed: who maintains the agent, what it can do, how many people it reaches, that the reach
   * grows without asking, and that stopping does not recall answers already given.
   */
  lend: {
    action: (base: string): string => `Let it answer from ${base}`,
    title: (base: string): string => `Let this agent answer from ${base}?`,
    lead: 'Read what this means before you decide. Only the people who maintain the knowledge base can allow it.',
    /**
     * The whole decision in one sentence — and the record of it (PRD-27 D-7).
     *
     * `ownerKind`/`ownerName` replace an `owner` parameter that arrived as "Finance's managers",
     * an English possessive dropped into the middle of a sentence. A language that says the same
     * thing with a relative clause cannot take it, and a RECORD holding it could only ever be
     * read back in English (D-20).
     */
    sentence: (fmt: Counting, input: AckPayloads['ack.lending_from_builder']): string => {
      const owner = input.ownerKind === 'group' ? maker.owner.managersOf(input.ownerName) : input.ownerName
      return [
        `${input.agent} will answer from ${input.base} — with passages and citations — for everyone who can use ${input.agent}: ${maker.people(fmt, input.reach)} with the choice on this page, ${fmt.formatCount(input.beyond)} of whom cannot use ${input.base} themselves.`,
        `They still cannot open, browse or search ${input.base}, or get it through any other agent.`,
        `When ${input.agent} is shared more widely, it answers from ${input.base} for those people too, without asking you again.`,
        `${input.agent} is maintained by ${owner}${input.acts ? ', and it can take actions — it always asks first' : ''}.`,
        'Stopping later does not take back answers already given.'
      ].join(' ')
    },
    ack: 'I maintain this knowledge base, and I allow it.',
    confirm: 'Allow',
    allowing: 'Allowing…',
    cancel: 'Cancel',
    failed: 'That could not be allowed.'
  }
}
